import { Check, KeyRound, Palette, Plus, SlidersHorizontal } from "lucide-react";
import { Link } from "react-router-dom";
import { errorMessage } from "../api/client";
import { useOrg, useOrgId } from "../api/org";
import { useCreateEmbedKey, useEmbedKeys, useLeads, useLuxWallet } from "../api/queries";
import { fmtLux } from "../lib/lux";
import { InstallSnippets } from "../components/InstallSnippets";
import { OriginsEditor } from "../components/OriginsEditor";
import { Alert, Badge, Button, Card, CopyButton, Spinner, cx, useToast } from "../components/ui";
import { useT, useIntlShape } from "../i18n/I18nProvider";

function Step({ n, done, title, description, children }: {
  n: number;
  done?: boolean;
  title: string;
  description?: string;
  children?: React.ReactNode;
}) {
  const t = useT();
  return (
    <div className={cx("step", done && "done")}>
      <div className="step-num">{done ? <Check /> : n}</div>
      <div>
        <div className="spread">
          <h3>{title}</h3>
          {done && <Badge tone="ok">{t("common.done")}</Badge>}
        </div>
        {description && <p className="muted" style={{ marginTop: 2 }}>{description}</p>}
        {children && <div className="step-body">{children}</div>}
      </div>
    </div>
  );
}

export function GetStarted() {
  const t = useT();
  const intl = useIntlShape();
  const orgId = useOrgId();
  const { membership, can } = useOrg();
  const canManage = can("widget_keys", "edit");
  const keys = useEmbedKeys(orgId);
  const wallet = useLuxWallet(orgId);
  const leads = useLeads(orgId, { source: "website" }, 1);
  const create = useCreateEmbedKey(orgId);
  const toast = useToast();

  if (keys.isLoading) return <Spinner />;
  const live = keys.data?.items.find((k) => k.mode === "live" && k.active) ?? keys.data?.items.find((k) => k.active);
  const hasOrigins = !!live?.allowed_origins.length;
  const hasLead = !!leads.data?.items.length;
  const balance = wallet.data?.balance ?? 0;

  return (
    <div className="stack-lg">
      <section className="hero">
        <span className="badge grad plain">{t("auth.getStarted.welcome")}</span>
        <h1 style={{ marginTop: 12 }}>
          {membership?.organization_name ? t("auth.getStarted.titleOrg", { org: membership.organization_name }) : t("auth.getStarted.title")}
        </h1>
        <p>{t("auth.getStarted.lead")}</p>
        <div className="hero-stats">
          <div className="hero-stat">
            <b>{fmtLux(balance)}</b>
            <span>{t("auth.getStarted.luxReady")}</span>
          </div>
          <div className="hero-stat">
            <b>{live ? (live.mode === "live" ? t("auth.getStarted.keyLive") : t("auth.getStarted.keyTest")) : "—"}</b>
            <span>{t("auth.getStarted.embedKey")}</span>
          </div>
          <div className="hero-stat">
            <b>{intl.formatNumber(live?.allowed_origins.length ?? 0)}</b>
            <span>{t("auth.getStarted.websitesAllowed", { count: live?.allowed_origins.length ?? 0 })}</span>
          </div>
        </div>
      </section>

      {!live ? (
        <Card title={t("auth.getStarted.yourKey")}>
          <div className="spread">
            <p className="muted">{t("auth.getStarted.noKey")}</p>
            <Button
              variant="primary"
              icon={<Plus />}
              disabled={!canManage}
              loading={create.isPending}
              onClick={() =>
                create.mutate({ label: t("auth.onboarding.keyLabel"), mode: "live", allowed_origins: [] }, { onError: (e) => toast(errorMessage(e), true) })
              }
            >
              {t("auth.getStarted.createKey")}
            </Button>
          </div>
          <div className="divider" style={{ margin: "16px 0" }} />
          <p className="muted" style={{ marginTop: 0 }}>{t("auth.getStarted.wordpress")}</p>
          <InstallSnippets
            embedKey={null}
            creatingKey={create.isPending}
            onCreateKey={
              canManage
                ? () => create.mutate({ label: t("auth.onboarding.keyLabel"), mode: "live", allowed_origins: [] }, { onError: (e) => toast(errorMessage(e), true) })
                : undefined
            }
          />
        </Card>
      ) : (
        <div className="split-wide">
          <Card pad={false}>
            <div className="card-body steps">
              <Step n={1} done={hasOrigins} title={t("auth.getStarted.step1.title")} description={t("auth.getStarted.step1.description")}>
                <OriginsEditor orgId={orgId} embedKey={live} canManage={canManage} />
              </Step>
              <div className="divider" />
              <Step n={2} done={hasLead} title={t("auth.getStarted.step2.title")} description={t("auth.getStarted.step2.description")}>
                <InstallSnippets embedKey={live.key} />
              </Step>
              <div className="divider" />
              <Step n={3} done={hasLead} title={t("auth.getStarted.step3.title")} description={t("auth.getStarted.step3.description")}>
                <div className="row">
                  <Link className="btn" to="/branding">
                    {t("auth.getStarted.preview")}
                  </Link>
                  <Link className="btn" to="/designs">
                    {t("auth.getStarted.seeLeads")}
                  </Link>
                </div>
              </Step>
            </div>
          </Card>
          <div className="stack">
            <Card title={t("auth.getStarted.liveKey")} description={t("auth.getStarted.liveKeyDescription")}>
              <div className="stack" style={{ gap: 10 }}>
                <div className="keybox">
                  <KeyRound size={16} color="var(--primary)" />
                  <code dir="ltr">{live.key}</code>
                  <CopyButton text={live.key} />
                </div>
                <div className="row small muted">
                  <Badge tone={live.mode === "live" ? "ok" : "warn"}>{live.mode === "live" ? t("auth.getStarted.keyLive") : t("auth.getStarted.keyTest")}</Badge>
                  <span>{live.label || t("auth.onboarding.keyLabel")}</span>
                  <Link to="/widget" style={{ marginInlineStart: "auto" }}>
                    {t("auth.getStarted.manageKeys")}
                  </Link>
                </div>
              </div>
            </Card>
            <Card title={t("auth.getStarted.makeItYours")} description={t("auth.getStarted.makeItYoursDescription")}>
              <div className="stack" style={{ gap: 8 }}>
                <Link className="btn btn-block" to="/branding" style={{ justifyContent: "flex-start" }}>
                  <Palette /> {t("auth.getStarted.brandingLink")}
                </Link>
                <Link className="btn btn-block" to="/rules" style={{ justifyContent: "flex-start" }}>
                  <SlidersHorizontal /> {t("auth.getStarted.rulesLink")}
                </Link>
              </div>
            </Card>
            {balance <= 0 && (
              <Alert tone="warn" title={t("auth.getStarted.noLux")} action={<Link className="btn btn-sm" to="/lux">{t("auth.getStarted.addLux")}</Link>}>
                {t("auth.getStarted.noLuxBody")}
              </Alert>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
